import React from "react";
import { useLargeSellTrades, useTopTraders } from "../../../hooks"; // Adjust the import path
import {
  Pagination,
  Skeleton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Stack,
  Button
} from "@mui/material";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useBitcoinPrice } from '../../../state/context/bitcoin.context'; // Adjust path if needed

// Import moment locales
import 'moment/locale/zh-cn';


function transformToBTC(value) {
  const sats = value / 1000;
  const btc = sats / 100000000;
  return btc;
}

function formatNumber(n) {
  // Divide by 1e11
  n = n / 1e11;

  // Determine the appropriate suffix
  if (n >= 1e9) {
    return (n / 1e9).toFixed(2) + "B";
  } else if (n >= 1e6) {
    return (n / 1e6).toFixed(2) + "M";
  } else if (n >= 1e3) {
    return (n / 1e3).toFixed(2) + "K";
  } else {
    return n.toFixed(2);
  }
}


const CollapsibleTableRow = ({ trader }) => {
  const { price: btcPrice } = useBitcoinPrice();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Get locale for number formatting
  const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

  // --- Data processing ---
  const tokenIdForDisplay = trader.tokenid; // Verify correct ID property
  const tokenIdForLink = trader.token?.id;  // Verify correct ID property
  const tokenName = trader.token?.name;

  // Calculate BTC amount numerically first
  const btcAmount = transformToBTC(trader.amount_btc);
  // Calculate USD amount
  const amountUSD = btcPrice ? btcAmount * btcPrice : null;

  return (
    <TableRow
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1) !important"
        },
        backgroundColor: "inherit",
        transition: "background-color 0.2s ease-in-out",
      }}
    >
      {/* Trader Cell */}
      <TableCell sx={{ py: 0.5, px: 1 }}>
        <Button
          variant="text" size="small"
          sx={{
            minWidth: 'auto',
            p: 0.5,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            textTransform: 'none',
            color: 'inherit'
          }}
          onClick={() => window.open(`/users/${ trader.user }`)}
        >
          {trader.user.slice(0, 3)}...{trader.user.slice(-3)}
        </Button>
      </TableCell>

      {/* Token Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
        <Button
          variant="text" size="small"
          sx={{
            minWidth: 'auto',
            p: 0.5,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            textTransform: 'none',
            color: 'inherit'
          }}
          onClick={() => tokenIdForLink && window.open(`/tokens/${ tokenIdForLink }`)}
          disabled={!tokenIdForLink}
        >
          <Avatar
            alt={tokenName || ''} src={`https://images.odin.fun/token/${ tokenIdForDisplay }`} // Verify ID
            sx={{ width: 14, height: 14, mr: 0.3 }} // Smaller avatar
          />
          <Typography variant="body2" component="span" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
            {tokenName ? (tokenName.length > 6 ? tokenName.slice(0, 6) + '...' : tokenName) : 'N/A'}
          </Typography>
        </Button>
      </TableCell>

      {/* Amount (Token) Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.65rem', fontWeight: 'bold' }}>
        {formatNumber(trader.amount_token)}
      </TableCell>

      {/* Amount (BTC/USD) Cell - UPDATED */}
      <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
        <Stack direction="column" spacing={0.2} alignItems="flex-end">
          {/* Display formatted BTC amount */}
          <Typography variant="body2" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
            {btcAmount.toFixed(4)} BTC
          </Typography>
          {/* Display formatted USD amount if available */}
          {amountUSD !== null && (
            <Typography variant="caption" sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: '0.6rem',
              fontWeight: 'bold'
            }}>
              ${amountUSD >= 1000 ? (amountUSD / 1000).toFixed(1) + 'K' : amountUSD.toFixed(0)}
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Time Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.65rem', fontWeight: 'bold' }}>
        {(() => {
          const timeAgo = moment(trader.time).fromNow();
          const timeDisplayMap = {
            'a few seconds ago': '1s',
            'a minute ago': '1m',
            'an hour ago': '1h',
            'a day ago': '1d'
          };

          // Check for exact matches first
          if (timeDisplayMap[timeAgo]) {
            return timeDisplayMap[timeAgo];
          }

          // Handle plurals and numbers
          return timeAgo
            .replace(/(\d+) seconds? ago/, '$1s')
            .replace(/(\d+) minutes? ago/, '$1m')
            .replace(/(\d+) hours? ago/, '$1h')
            .replace(/(\d+) days? ago/, '$1d')
            .replace(/(\d+) months? ago/, '$1mo')
            .replace(/(\d+) years? ago/, '$1y');
        })()}
      </TableCell>
    </TableRow>


  );
};

const SellTrades = ({ selectedTimeOffset }) => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, page, setPage, hasNextPage } =
    useLargeSellTrades(selectedTimeOffset, 10);
  const navigate = useNavigate();

  // Set moment locale based on current language
  React.useEffect(() => {
    const locale = i18n.language === 'zh' ? 'zh-cn' : 'en';
    moment.locale(locale);
  }, [i18n.language]);

  if (loading && !data.length)
    return (
      <Skeleton
        sx={{ flexGrow: 1 }}
        variant="rectangular"
        // width={210}
        // height={118}
        style={{ width: "100%", height: 300 }}
      />
    );
  if (error)
    return (
      <Typography color="error" align="center">
        {t('common.error')}: {error?.message}
      </Typography>
    );

  return (
    <Box sx={{ width: "100%", mx: "auto", overflow: "hidden", flexGrow: 1, display: "flex", flexDirection: "column" }}>
      {data.length === 0 ? (
        <Typography align="center">{t('common.noDataAvailable')}</Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.02)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.05)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:hover": {
            //   backgroundColor: "rgba(255, 255, 255, 0.1) !important",
            // },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }}>
                  <strong>{t('table.trader')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.token')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.amountToken')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.amountBTC')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.time')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((trader) => (
                <CollapsibleTableRow key={trader.id} trader={trader} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={hasNextPage ? page + 1 : page} // Assume one extra page exists
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size="small"
        />
      </Box>
    </Box>
  );
};

export default SellTrades;
